import environments from './XEnvironment.js';
import { xArray, xObject, xOptional, xReference, xString } from './XTypes.js';
import { xExtendClassForDOM, XTemplateDOM } from './XDOM.js';
import { xBase64ToObject, xObjectToBase64, xReplaceVariables } from './XUtil.js';

import templateWebStylesheet from './XPage_web_Stylesheet.html?raw';
import templateWebJavaScriptBody from './XPage_web_JavaScriptBody.html?raw';
import templateWebJavaScriptHead from './XPage_web_JavaScriptHead.html?raw';
import templateWeb from './XPage_web.html?raw';
import template from './XPage.html?raw';
import template_Settings from './XPage_Settings.html?raw';

import XResizeContainer from './XResizeContainer.js';

import { XPageMenuItemReactive } from './XPageMenuItem.js';
import { XPageSettingsSiteReactive } from './XPageSettingsSite.js';

export class XPage
{
    static get type () { return 'XPage'; }

    #dom;
    #storageData;

    #site;
    
    #pageMenu;
    #content;
    #currentPageMenuItem;
    
    #sectionResizeContainer;

    #elementNavbar;
    #elementMain;
    #elementScroller;
    #elementTOCCanvas;
    #elementTOC;

    #windowWidth = 0;
    #windowHeight = 0;
    #devicePixelRatio = 0;
    #navbarHeight = 0;
    #mainVisibleHeight = 0;
    #maxContentWidth = 0;

    #scheduledLayout = false;

    constructor(site)
    {
        this.#site = site;
    };

    initialize(dom)
    {
        this.#dom = dom;
        this.#storageData = xBase64ToObject(this.#dom.body._element.dataset.xStorage);
        
        //console.log(dom);
        //console.log(this.#storageData);

        const domTypes = environments.active.domTypes;

        const domNavbar = this.#dom.body.navbar;
        domNavbar.logo.image._setCreator(model => new domTypes.XImage(model, this));
        domNavbar.logoBrand.image._setCreator(model => new domTypes.XImage(model, this));

        const domMain = this.#dom.body.main;

        this.#elementNavbar    = domNavbar._element;
        this.#elementMain      = domMain._element;
        this.#elementScroller  = domMain.scroller._element;
        this.#elementTOCCanvas = domMain.tocCanvas._element;
        this.#elementTOC       = domMain.tocCanvas.toc._element;
        
        this.#sectionResizeContainer = new XResizeContainer(this.#elementMain);

        //const idOffcanvas = xRandomString();
        //domNavbar.menu._element.id = idOffcanvas;
    
        this.#pageMenu = domMain.pageMenu._setCreator(model => new domTypes.XPageMenuItem(model, this, this, this.#site));
        this.#content  = domMain.content._setCreator(model => new domTypes.XContent(model, this, this.#site));
        this.#currentPageMenuItem = null;
        this.pageMenu?.visit((item) => {
            if(item.isHighlighted)
            {
                this.#currentPageMenuItem = item;
            }
        });

        window.addEventListener('resize', () =>
        {
            this.onResize(true);
        });

        window.addEventListener('scroll', () =>
        {
            this.onScroll();
        });

        this.updateAnchors();
        this.scheduleLayout().then(() => {
            const startBlockID = window.location.hash.substring(1);
            window.location.hash = '';
            if(startBlockID)
            {
                this.scrollToBlock(startBlockID, true);
                setTimeout(() => {
                    this.scrollToBlock(startBlockID, true);
                }, 100);
            }
        });
    }

    scheduleLayout()
    {
        if(this.#scheduledLayout)
        {
            return this.#scheduledLayout;
        }
        this.#scheduledLayout = new Promise(resolve => {
            requestAnimationFrame(() => {
                this.onResize();
                this.onScroll();
                this.#scheduledLayout = null;
                resolve();
            });
        });
        return this.#scheduledLayout;
    }

    updateAnchors()
    {
        this.#elementTOC.innerHTML = '';
        let currentHeadingType = 0;
        const elementStack = [this.#elementTOC];
        if(this.content)
        {
            for(const section of this.content.sections)
            {
                let blockCurrentHeading = null;
                for(const block of section.blocksFlattened)
                {
                    const tagName = block.element.tagName;
                    block.elementAnchor = undefined;
                    if(/^H\d$/.test(tagName))
                    {
                        const headingType = parseInt(tagName[1]);
                        for(let i = currentHeadingType; i < headingType; i++)
                        {
                            const elementList = document.createElement('ul');
                            elementStack[elementStack.length-1].append(elementList);
                            elementStack.push(elementList);
                        }
                        for(let i = currentHeadingType; i > headingType; i--)
                        {
                            elementStack.pop();
                        }
                        const elementCurrentList = elementStack[elementStack.length-1];
                        const elementAnchor = document.createElement('div');
                        elementAnchor.innerHTML = block.textVoice.elementText.innerHTML;
                        elementAnchor.addEventListener('click', () => {
                            this.pageMenu.hideRoot();
                            block.scrollHere();
                        });
                        elementCurrentList.append(elementAnchor);
                        block.elementAnchor = elementAnchor;
                        currentHeadingType = headingType;
                        blockCurrentHeading = block;
                    }
                    if(blockCurrentHeading)
                    {
                        blockCurrentHeading.blockEndAnchor = block;
                    }
                }
            }
        }
    }

    unloadSectionMedia()
    {
        for(const section of this.content.sections)
        {
            section.unloadMedia();
        }
    }

    onResize(onlyIfSizeChanged=false)
    {
        if(this.content)
        {
            //const width = window.innerWidth;
            //const height = window.innerHeight;
            const documentBoundingClientRect = document.documentElement.getBoundingClientRect();
            const width = documentBoundingClientRect.width;
            const height = documentBoundingClientRect.height;
            if(onlyIfSizeChanged && width == this.#windowWidth && height == this.#windowHeight)
            {
                return;
            }
            const currentScrollTop = document.documentElement.scrollTop;
            window.scrollTo({ top:0, left:0, behavior: "instant"});
            this.#windowWidth = width;
            this.#windowHeight = height;
            this.#devicePixelRatio = window.devicePixelRatio;
            this.#navbarHeight = this.#elementNavbar.getBoundingClientRect().height;
            this.#mainVisibleHeight = this.#windowHeight - this.#navbarHeight;
            this.#maxContentWidth = Math.min(this.#windowWidth, 16/18 * this.#mainVisibleHeight);

            if(this.#windowWidth > 1400)
            {
                this.#elementTOCCanvas.append(this.#elementTOC);
            }
            else
            {
                this.#currentPageMenuItem?.elementMenuItem?.after(this.#elementTOC);
            }
            
            const currentSourceScroll = this.content.onResize();
            this.#elementScroller.classList.remove('d-none');
            this.#elementScroller.style.height = String(this.#mainVisibleHeight + currentSourceScroll) + 'px';

            if(this.content?.blockFocus)
            {
                this.content.blockFocus.scrollHere(true);
            }
            else
            {
                window.scrollTo({ top:currentScrollTop, left:0, behavior: "instant"});
            }
        }
        else
        {
            this.#elementScroller.classList.add('d-none');
        }
    }

    onScroll()
    {
        if(this.content)
        {
            const scrollerRect = this.#elementScroller.getBoundingClientRect();
            //const contentScrollHeight = this.#navbarHeight + scrollerRect.height - this.#windowHeight;
            const currentContentScrollPosition = this.#navbarHeight - scrollerRect.top;
            this.content.onScroll(currentContentScrollPosition);
        }
    };

    enableContent()
    {
        this.#content._enable();
        this.updateAnchors();
        this.onResize();
    }

    disableContent()
    {
        this.#content._disable();
        this.onResize();
    }

    scrollToBlock(id, instant=false)
    {
        if(this.content)
        {
            for(const section of this.content.sections)
            {
                for(const block of section.blocksFlattened)
                {
                    if(block.id == id)
                    {
                        block.scrollHere(instant);
                        return;
                    }
                }
            }
        }
    }

    getDataForKey(key) 
    {
        return this.#storageData[key];   
    }

    set currentPageMenuItem (currentPageMenuItem) { this.#currentPageMenuItem = currentPageMenuItem; }

    get _dom                   () { return this.#dom;                    }
    get site                   () { return this.#site;                   }
    get pageMenu               () { return this.#pageMenu?.mount;        }
    get content                () { return this.#content?.mount;         }
    get currentPageMenuItem    () { return this.#currentPageMenuItem;    }
    get sectionResizeContainer () { return this.#sectionResizeContainer; }
    get elementNavbar          () { return this.#elementNavbar;          }
    get elementMain            () { return this.#elementMain;            }
    get elementScroller        () { return this.#elementScroller;        }
    get windowWidth            () { return this.#windowWidth;            }
    get windowHeight           () { return this.#windowHeight;           }
    get devicePixelRatio       () { return this.#devicePixelRatio;       }
    get navbarHeight           () { return this.#navbarHeight;           }
    get mainVisibleHeight      () { return this.#mainVisibleHeight;      }
    get maxContentWidth        () { return this.#maxContentWidth;        }
};

export class XPageReactive extends XPage
{
    static get specification () 
    {
        return xObject({
            $: XPage.type,
            site: xReference('XPageSettingsSite'),
            pageMenu: xReference('XPageMenuItem'),
            content: xReference('XContent'),
            seo: xReference('XPageSettingsSEO'),
        });
    }

    static #template;
    static #templateSettings;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XPageReactive.specification._shallowMerge({
            navbar: XPageSettingsSiteReactive.specification.navbar,
            footer: XPageSettingsSiteReactive.specification.footer
        }), domEnvironment);
        this.#templateSettings = new XTemplateDOM(template_Settings, xObject({
            pageMenuItem: XPageMenuItemReactive.specification,
            seo: xReference('XPageSettingsSEO'),
        }), domEnvironment);
        console.log(this.#templateSettings);
        /*
        this.#templateSettings = new XTemplateDOM(template_Settings, xObject({
            menuItem: xReference('XPageMenuItem'),
            seo: xReference('XPageSettingsSEO'),
        }));
        */
    }

    #data;
    #pageSettings;

    constructor(data, site)
    {
        super(site);
        this.#data = data._shallowMerge({
            navbar: data.site.navbar,
            footer: data.site.footer
        });
        this.#data._key = data._key;
        this.#data._version = data._version;
        this.#data.content_ = this.#data.content_.clone();
        
        super.initialize(XPageReactive.#template._createAccessibleDOM(this.#data));
        
        this._dom.body.navbar.logo._attachData(this.#data.navbar.logo.url_, {
            _setValue: url => {
                this._dom.body.navbar.logo._element.href = url._value
            }
        });
        this._dom.body.navbar.links._addAttachment({
            _create: dom => {
                dom.label._attachData(dom._data.url_, {
                    _setValue: url => {
                        dom.label._element.href = url._value
                    }
                });
            }
        });
        this._dom.body.navbar.languageSwitcher.languages._addAttachment({
            _create: dom => {
                dom.label._element.addEventListener('click', (event) => 
                {
                    this.site.storage.locale = dom._data.locale;
                    event.preventDefault();
                });
            }
        });
        this._dom.body.navbar.languageSwitcher.dropdown._bindComputed(xString(), locale => this.#data.navbar.languages.find(element => element.locale == locale._value)?.label ?? '<i class="bi bi-translate"></i>', this.site.storage.locale_);

        this._dom.body.navbar.translator.save._element.addEventListener('click', () => 
        {
            environments.active.api.save().then(() => {
                this._dom.body.navbar.translator.save._element.classList.add('text-success');
                this._dom.body.navbar.translator.save._element.classList.remove('text-danger');
            });
        });

        this._dom.body._element.addEventListener('input', () => {
            this._dom.body.navbar.translator.save._element.classList.remove('text-success');
            this._dom.body.navbar.translator.save._element.classList.add('text-danger');
        });

        this.currentPageMenuItem = null;
        this.pageMenu?.visit((item) => {
            if(item.data.page_._key == this.#data._key)
            {
                this.currentPageMenuItem = item;
            }
        });
        this.currentPageMenuItem?.highlight(false);

        this._dom.body.navbar.titleMenu._bind(this.#data.site?.titleMenu_);
        this._dom.body.main.tocCanvas.titleTOC._bind(this.#data.site?.titleTOC_);

        this.#pageSettings = this._dom.body.main.pageSettings;

        this._dom.body.footer.updateTag._bindComputed(xString(), (label, locale) => xReplaceVariables(label._value, {
                date: (time => '<time datetime=' + time.toISOString() + '>' + time.toLocaleDateString(locale._value, { year: 'numeric', month: 'long', day: 'numeric' }) + '</time>')(new Date())
        }), this.#data.footer.updated.label_, this.#data.footer.updated.locale_);
        
        this._dom.body.footer.links._addAttachment({
            _create: dom => {
                dom.label._attachData(dom._data.url_, {
                    _setValue: url => {
                        dom.label._element.href = url._value
                    }
                });
            }
        });

        this._dom._attachData(this.site.storage.locale_, {
            _setValue: () => {
                this.updateAnchors();
                this.unloadSectionMedia();
                this.onResize();
                this.onScroll();
            }
        }, false);
    };

    showContent(pageMenuItem)
    {
        if(pageMenuItem)
        {
            this.currentPageMenuItem = pageMenuItem;
            this.#data.content = pageMenuItem.data.page.content_.toString();
        }
        this.#pageSettings._unmount();
        pageMenuItem.highlight(false);
        this.enableContent();
    }

    showSettings(pageMenuItem)
    {
        this.disableContent();
        this.#pageSettings._unmount();
        const menuPath = pageMenuItem.path.slice(1,-1).map(item => item.data.route_);
        const settings = xObject({
            pageMenuItem: pageMenuItem.data._shallowMerge({
                urlRoot: this._dom.body.main.pageSettings._computedOutput(xString(), (urlRoot, ...menuPath) => 
                    {
                        let path = menuPath.map(route => route._value).join('/');
                        if(path)
                        {
                            path += '/';
                        }
                        return urlRoot._value + path;
                    }, environments.active.urlRoot_, ...menuPath) 
            })
        });
        const page = pageMenuItem.data.page;
        if(page)
        {
            settings._add('seo', pageMenuItem.data.page.seo_);
        }
        this.#pageSettings._mount(() => {
            const domSettings = XPageReactive.#templateSettings._createAccessibleDOM(settings);
            const domPageMenuItem = domSettings.pageMenuItem.mount;
            if(this.site.isEditor)
            {
                domPageMenuItem.actions.delete._element.addEventListener('click', (event) => 
                {
                    this.showSettings(pageMenuItem.remove());
                });
            }
            if(this.site.isTranslator)
            {
                domPageMenuItem.actions._destroy();
                domPageMenuItem.route._element.disabled = true;
            }
            return domSettings;
        });
        if(page)
        {
            this.#pageSettings.mount.seo._setCreator(model => new environments.active.domTypes.XPageSettingsSEO(model, this));
        }
        pageMenuItem.highlight(false);
    }

    createNewData()
    {
        const page = this.site.storage.addData('XPage');
        const content = this.site.storage.addData('XContent');
        const section = this.site.storage.addData('XSection');
        const block = this.site.storage.addData('XBlock');
        const textVoice = this.site.storage.addData('XTextVoice');
        page.site = this.#data.site_._key;
        page.pageMenu = this.#data.pageMenu_._key;
        page.content = content._key;
        page.seo = this.site.storage.addData('XPageSettingsSEO')._key;
        content.sections.push(section._key);
        section.blocks.push(block._key);
        block.type = 'h1';
        block.textVoice = textVoice._key;
        textVoice.text = 'NEW SECTION';
        return page;
    }

    collectStaticStorageIDs(collectedIDs)
    {
        this.content.collectStaticStorageIDs(collectedIDs);
    }

    _forceInitialLayout()
    {
        this._dom.body.main.tocCanvas._element.append(this._dom.body.main.tocCanvas.toc._element);
    }

    _render(dom)
    {
        const collectedIDs = new Set();
        this.collectStaticStorageIDs(collectedIDs);
        const staticStorage = {};
        for(const collectedID of collectedIDs)
        {
            const dataReactive = this.site.storage.data[collectedID];
            if(dataReactive)
            {
                const data = dataReactive.toJSON();
                data.$key = dataReactive._key;
                data.$version = dataReactive._version;
                staticStorage[collectedID] = data;
            }
        }
        dom.body._element.dataset.xStorage = xObjectToBase64(staticStorage);
        return dom;
    }

    getDataForKey(key)
    {
        const dataReactive = this.site.storage.data[key];
        if(!dataReactive)
        {
            return undefined;
        }
        const data = this.site.storage.data[key].toJSON();
        data.$key = dataReactive._key;
        data.$version = dataReactive._version;
        return data;
    }

    get data () { return this.#data; }
}

export function buildWeb(data)
{
    const htmlStylesheet = xReplaceVariables(templateWebStylesheet, {
        urlRoot: '/'
    });
    const htmlJavaScriptHead = xReplaceVariables(templateWebJavaScriptHead, {
        urlRoot: '/'
    });
    const htmlJavaScriptBody = xReplaceVariables(templateWebJavaScriptBody, {
        urlRoot: '/'
    });
    return xReplaceVariables(templateWeb, {
        Stylesheet: htmlStylesheet,
        JavaScriptHead: htmlJavaScriptHead,
        JavaScriptBody: htmlJavaScriptBody,
    });
}
